import { User } from 'module/api/user/types';
import { fullName } from 'module/helpers/full-name';
import { getRandomColor } from 'module/helpers/get-random-color';
import {
  EmptyState,
  EmptyStateContainer,
} from 'module/ui/components/atoms/empty-state';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { Label01 } from 'module/ui/components/styles';
import { faPage } from '@fortawesome/pro-regular-svg-icons';
import { Color, Tag } from '@rocketlane/ui';
import { Editor as TiptapEditor, Extension } from '@tiptap/core';
import Collaboration from '@tiptap/extension-collaboration';
import CollaborationCaret from '@tiptap/extension-collaboration-caret';
import { DecorationAttrs } from '@tiptap/pm/view';
import StarterKit from '@tiptap/starter-kit';
import { yXmlFragmentToProsemirrorJSON } from '@tiptap/y-tiptap';
import { TiptapCollabProvider } from '@tiptap-pro/provider';
import { PagesAPI } from 'api/pages';
import { TIPTAP_COLLAB_BASE_URL } from 'config/environment';
import { useCurrentUserQuery } from 'queries/account';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Doc } from 'yjs';
import { DocumentEditor } from './document-editor';
import {
  defaultExtensionConfig,
  DocumentEditorProps,
  ExtensionConfig,
} from './types';
import { RichTextEditorValueType } from '../../../module/ui/components/atoms/rich-text-editor/types';

const Editor = styled(DocumentEditor)`` as typeof DocumentEditor;

const UserTag = styled(Tag)`
  border-bottom-left-radius: 0;
  max-width: unset;
`;
const Wrapper = styled.span<{ $color: Color }>`
  --cursor-position-bg-color: ${({ $color }) => `var(--${$color}-60)`};
  border-left: 1px solid var(--cursor-position-bg-color);
  border-right: 2px solid var(--cursor-position-bg-color);
  border-color: var(--cursor-position-bg-color);
  margin-left: -1px;
  margin-right: -2px;
  position: relative;
  word-break: normal;
  ${UserTag} {
    background-color: var(--cursor-position-bg-color);
    color: var(--white-100);
    position: absolute;
    top: calc(-1rem - 2px);
    left: -1px;
    ${Label01};
  }
`;
const LoadingMessage = styled.span`
  animation: fadeInOut 2s ease-in-out infinite;
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.2) 0%,
    rgba(0, 0, 0, 1) 50%,
    rgba(0, 0, 0, 0.2) 100%
  );
  background-size: 200% 100%;
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  @keyframes fadeInOut {
    0% {
      background-position: 100% 50%;
    }
    100% {
      background-position: -100% 50%;
    }
  }
`;
const LoadingWrapper = styled(EmptyStateContainer)`
  min-height: 300px;
`;

const UserCursorPosition = ({ name, color, actualUser }: CollaborationUser) => {
  return (
    <Wrapper $color={color} data-collaboration-user-id={actualUser.userId}>
      <UserTag variant="basic" color={color}>
        {name}
      </UserTag>
    </Wrapper>
  );
};

export type CollaborationUser = {
  actualUser: User;
  color: Color;
  name: string;
  cursor?: any;
};

type CollaborationConfig =
  | {
      enabled: false;
    }
  | {
      enabled: true;
      documentName: string;
      onActiveUsersChange?: (users: CollaborationUser[]) => void;
    };
type CollaborativeEditorExtensionConfig = ExtensionConfig & {
  collaborationConfig: CollaborationConfig;
};

const defaultCollaborativeEditorExtensionConfig: CollaborativeEditorExtensionConfig =
  {
    ...defaultExtensionConfig,
    collaborationConfig: {
      enabled: false,
    },
  };

type CollaborationState =
  | {
      isInitialized: false;
      provider: null;
    }
  | {
      isInitialized: true;
      provider: TiptapCollabProvider;
      extensions: Extension[];
      contentLoaded: boolean;
      /** Content from Yjs after sync; used as initial editor value so the editor shows synced content */
      yjsInitialContent?: Record<string, unknown>;
    };

interface CollaborativeEditorProps<ValueType extends RichTextEditorValueType>
  extends Omit<DocumentEditorProps<ValueType>, 'extensionConfig'> {
  extensionConfig: Partial<CollaborativeEditorExtensionConfig>;
  isChatOpen?: boolean;
}

function BaseCollaborativeEditor<ValueType extends RichTextEditorValueType>({
  extensionConfig:
    extensionConfigProp = defaultCollaborativeEditorExtensionConfig,
  value,
  onInitialize: onInitializeProp,
  isChatOpen,
  pageType,
  ...editorProps
}: CollaborativeEditorProps<ValueType>) {
  const { data: userProfile, isLoading: isUserProfileLoading } =
    useCurrentUserQuery();
  const [editor, setEditor] = useState<TiptapEditor | null>(null);
  const editorRef = useRef<TiptapEditor | null>(null);

  editorRef.current = editor;

  const extensionConfig = useMemo(() => {
    return {
      ...defaultCollaborativeEditorExtensionConfig,
      ...extensionConfigProp,
    };
  }, [extensionConfigProp]);
  const isCollaborationEnabled = extensionConfig.collaborationConfig.enabled;
  const [collaborationState, setCollaborationState] =
    useState<CollaborationState>({
      isInitialized: false,
      provider: null,
    });
  const onInitialize = useCallback(
    (editor: TiptapEditor) => {
      setEditor(editor);
      if (!isCollaborationEnabled) {
        onInitializeProp?.(editor);
      }
    },
    [isCollaborationEnabled, onInitializeProp],
  );

  // Use ref to store current state for cleanup
  const collaborationStateRef = useRef(collaborationState);

  collaborationStateRef.current = collaborationState;
  const initializeCollaboration = useCallback(async () => {
    if (!extensionConfig.collaborationConfig.enabled || !userProfile?.user) {
      return;
    }

    const collaborationDoc = new Doc();
    const provider = new TiptapCollabProvider({
      name: extensionConfig.collaborationConfig.documentName,
      baseUrl: TIPTAP_COLLAB_BASE_URL,
      token: async () => {
        const token = await PagesAPI.getCollaborationToken();

        return token.collabToken;
      },
      document: collaborationDoc,
      onAwarenessChange: (params) => {
        if (extensionConfig.collaborationConfig.enabled) {
          extensionConfig.collaborationConfig.onActiveUsersChange?.(
            params.states.map((state) => {
              return {
                ...state.user,
                cursor: state.cursor,
              };
            }),
          );
        }
      },
      onSynced() {
        // Content from Yjs (same fragment the Collaboration extension uses, field 'default')
        const yjsFragment = collaborationDoc.getXmlFragment('default');
        const contentFromYjs = yXmlFragmentToProsemirrorJSON(yjsFragment);

        onInitializeProp?.(editorRef.current!);
        setCollaborationState((prev) => ({
          ...prev,
          contentLoaded: true,
          yjsInitialContent: contentFromYjs,
        }));
        if (
          !collaborationDoc.getMap('config').get('initialContentLoaded') &&
          editorRef.current
        ) {
          collaborationDoc.getMap('config').set('initialContentLoaded', true);

          if (value) {
            editorRef.current.commands.setContent(value);
          }
        }
      },
    });
    const userName = fullName(userProfile.user);
    const color = getRandomColor(
      new Date().getTime() + userProfile.user.userId,
    );

    setCollaborationState({
      isInitialized: true,
      provider,
      contentLoaded: false,
      extensions: [
        Collaboration.configure({
          document: collaborationDoc,
        }),
        CollaborationCaret.configure({
          provider,
          render: (params: CollaborationUser) => {
            const element = document.createElement('span');
            const root = createRoot(element);

            root.render(<UserCursorPosition {...params} />);

            return element;
          },
          selectionRender: (params: CollaborationUser): DecorationAttrs => {
            return {
              style: `
                background-color: var(--${params.color}-20);
              `,
            };
          },
          user: {
            actualUser: userProfile.user,
            name: userName,
            color,
          },
        }),
        StarterKit.configure({
          undoRedo: false,
        }),
      ],
    });
  }, [
    extensionConfig.collaborationConfig,
    onInitializeProp,
    userProfile,
    value,
  ]);

  useEffect(() => {
    if (
      isCollaborationEnabled &&
      !collaborationState.isInitialized &&
      !isUserProfileLoading
    ) {
      initializeCollaboration();
    }
  }, [
    isCollaborationEnabled,
    initializeCollaboration,
    collaborationState.isInitialized,
    isUserProfileLoading,
  ]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      const currentState = collaborationStateRef.current;

      if (currentState.isInitialized && currentState.provider) {
        currentState.provider.awareness?.setLocalState(null);
        currentState.provider.destroy();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      const currentState = collaborationStateRef.current;

      if (currentState.isInitialized && currentState.provider) {
        currentState.provider.awareness?.setLocalState(null);
        currentState.provider.destroy();
      }
    };
  }, []);

  const extensions = useMemo(() => {
    return collaborationState.isInitialized
      ? [...collaborationState.extensions, ...(editorProps.extensions ?? [])]
      : (editorProps.extensions ?? []);
  }, [collaborationState, editorProps.extensions]);

  const isContentReady = useMemo(
    () =>
      isCollaborationEnabled
        ? collaborationState.isInitialized &&
          'contentLoaded' in collaborationState &&
          collaborationState.contentLoaded
        : true,
    [collaborationState, isCollaborationEnabled],
  );

  const isLoadingContent = useMemo(
    () =>
      collaborationState.isInitialized &&
      'contentLoaded' in collaborationState &&
      !collaborationState.contentLoaded,
    [collaborationState],
  );

  if (isCollaborationEnabled && !collaborationState.isInitialized) {
    return (
      <LoadingWrapper>
        <EmptyState
          image={
            <FontAwesomeIcon
              icon={faPage}
              size={50}
              iconSize={40}
              color="transparent"
            />
          }
          message={
            <LoadingMessage>
              <FormattedMessage id="document.editor.collaboration.loading" />
            </LoadingMessage>
          }
        />
      </LoadingWrapper>
    );
  }

  return (
    <>
      {isLoadingContent && (
        <LoadingWrapper>
          <EmptyState
            image={
              <FontAwesomeIcon
                icon={faPage}
                size={50}
                iconSize={40}
                color="transparent"
              />
            }
            message={
              <LoadingMessage>
                <FormattedMessage id="document.editor.collaboration.loading.content" />
              </LoadingMessage>
            }
          />
        </LoadingWrapper>
      )}
      {isContentReady && (
        <Editor
          {...editorProps}
          extensionConfig={extensionConfig}
          value={
            isCollaborationEnabled &&
            collaborationState.isInitialized &&
            'yjsInitialContent' in collaborationState &&
            collaborationState.yjsInitialContent != null
              ? (collaborationState.yjsInitialContent as ValueType)
              : isCollaborationEnabled
                ? undefined
                : value
          }
          extensions={extensions}
          onInitialize={onInitialize}
          isChatOpen={isChatOpen}
          pageType={pageType}
        />
      )}
    </>
  );
}
const StyledCollaborativeEditor = styled(BaseCollaborativeEditor)``;

export const CollaborativeEditor: typeof BaseCollaborativeEditor =
  StyledCollaborativeEditor;
